import React from 'react'
import {useTranslation} from 'react-i18next'
import {InvitationCodeRecord} from '../../domain/entities/InvitationCode.ts'
import {CodeGroupKind} from './unifyCodeRows.ts'
import {AddPill} from './PackActivityTables.tsx'
import IconGroup from '../../assets/icons/grupo.svg?react'
import IconChevronDown from '../../assets/icons/chevron-down.svg?react'

// Group-album pieces shared by the agency (AgencySelfServiceCodeSections) and
// 51Trips (PackActivityTables) code tables — both artboards draw them alike.

// Design tints: the pill in "Estado" and the badge next to a code use the same pair.
const GROUP_TONE: Record<CodeGroupKind, string> = {
    individual: 'bg-[#EEF0F8] border-[#3E4878]/[.16] text-[#2E3A6B]',
    shared: 'bg-[#F5EAF7] border-[#9A4FAD]/[.22] text-[#6E2F82]',
}

const useGroupLabel = (kind: CodeGroupKind) => {
    const {t} = useTranslation('dashboard')
    return kind === 'shared' ? t('packs.groupSharedAlbum') : t('packs.groupIndividualAlbum')
}

export const GroupPill: React.FC<{kind: CodeGroupKind}> = ({kind}) => (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${GROUP_TONE[kind]}`}>
        <IconGroup className="w-[18px] h-[18px] flex-none"/>
        {useGroupLabel(kind)}
    </span>
)

export const GroupBadge: React.FC<{kind: CodeGroupKind}> = ({kind}) => (
    <span title={useGroupLabel(kind)}
          className={`w-[26px] h-[26px] flex-none inline-flex items-center justify-center rounded-full border ${GROUP_TONE[kind]}`}>
        <IconGroup className="w-4 h-4"/>
    </span>
)

// Lead cell of a collapsible group row: chevron + "N códigos".
export const GroupToggle: React.FC<{isOpen: boolean; count: number}> = ({isOpen, count}) => {
    const {t} = useTranslation('dashboard')
    return (
        <span className="inline-flex items-center gap-[9px] whitespace-nowrap">
            <span className="w-6 h-6 flex-none inline-flex items-center justify-center rounded-full bg-neutralBlack-100 text-neutralBlack-700">
                <IconChevronDown className={`w-2.5 h-2.5 transition-transform duration-200 ease-out ${isOpen ? '' : '-rotate-90'}`}/>
            </span>
            <span className="text-[13px] text-neutralBlack-500">{t('packs.groupCodesCount', {count})}</span>
        </span>
    )
}

// A group member's code, indented under its group row.
export const MemberCode: React.FC<{code: string | null}> = ({code}) => (
    <span className="inline-flex items-center pl-3 border-l-2 border-[#DBDBDB] font-medium text-neutralBlack-900 tracking-wide whitespace-nowrap">
        {code ?? <span className="text-neutralBlack-400">—</span>}
    </span>
)

// A shared album's "Viajero": "Lista de viajeros (x/N)", or the dashed pill to
// start one. Both open SharedAlbumTravelersDialog.
export const TravelerListButton: React.FC<{record: InvitationCodeRecord; onOpen: () => void}> = ({record, onOpen}) => {
    const {t} = useTranslation('dashboard')
    const count = record.travelers?.length ?? 0
    return (
        <button type="button" onClick={(e) => {e.stopPropagation(); onOpen()}}>
            {count > 0 ? (
                <span className="inline-flex items-center gap-[7px] text-[13px] font-medium text-[#3E63DD] hover:underline whitespace-nowrap">
                    <IconGroup className="w-3.5 h-3.5 flex-none"/>
                    {t('packs.travelerListLink', {count, total: record.copies ?? 1})}
                </span>
            ) : <AddPill label={t('packs.addTravelers')}/>}
        </button>
    )
}
